import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trophy, Plus, Pencil, Trash2, Loader2, Send, Filter, X, RefreshCw,
  CalendarDays, Settings2, CheckSquare, Square, AlertTriangle, Users,
  ChevronUp, ChevronDown, ChevronsUpDown,
} from 'lucide-react';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API } from '../utils/apiConfig';
import { useMasterData } from '../contexts/MasterDataContext';

const err = (e) => e?.response?.data?.detail || e?.message || 'Error';
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;
const STATUS_ORDER = ['quoted', 'booked', 'cancelled', 'completed'];
const STATUS_COLORS = {
  quoted: 'bg-slate-100 text-slate-700',
  booked: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-rose-100 text-rose-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

// datetime-local requires `YYYY-MM-DDTHH:MM` (no seconds, no tz)
const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function VenueEventsPage() {
  const { getVenueNames } = useMasterData();
  const venueNames = useMemo(() => getVenueNames() || [], [getVenueNames]);
  const [tab, setTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [kpis, setKpis] = useState({ total: 0, by_status: {}, revenue: 0 });
  const [loading, setLoading] = useState(false);
  const [addons, setAddons] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [settings, setSettings] = useState({ reminder_days: [7, 3, 1, 0] });
  const [recipients, setRecipients] = useState([]);

  // Filters
  const [fVenue, setFVenue] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fOrganizer, setFOrganizer] = useState('');
  const [fType, setFType] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [fDateRange, setFDateRange] = useState('all');
  const [sortKey, setSortKey] = useState('start_at');
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = (key) => {
    if (sortKey === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(key); setSortDir('asc'); }
  };

  // Date-range preset → sets fFrom/fTo (skipped when the user picks Custom).
  useEffect(() => {
    if (fDateRange === 'custom') return;
    const iso = (d) => {
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    };
    const now = new Date();
    if (fDateRange === 'all') { setFFrom(''); setFTo(''); return; }
    if (fDateRange === 'this_month') {
      setFFrom(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
      setFTo(iso(new Date(now.getFullYear(), now.getMonth() + 1, 0))); return;
    }
    if (fDateRange === 'next_month') {
      setFFrom(iso(new Date(now.getFullYear(), now.getMonth() + 1, 1)));
      setFTo(iso(new Date(now.getFullYear(), now.getMonth() + 2, 0))); return;
    }
    if (fDateRange === 'last_month') {
      setFFrom(iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
      setFTo(iso(new Date(now.getFullYear(), now.getMonth(), 0))); return;
    }
    if (fDateRange === 'this_year') {
      setFFrom(iso(new Date(now.getFullYear(), 0, 1)));
      setFTo(iso(new Date(now.getFullYear(), 11, 31))); return;
    }
  }, [fDateRange]);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const buildFilterParams = useCallback(() => {
    const p = {};
    if (fVenue && fVenue !== 'all') p.venue = fVenue;
    if (fStatus && fStatus !== 'all') p.status = fStatus;
    if (fOrganizer) p.organizer = fOrganizer;
    if (fType) p.event_type = fType;
    if (fFrom) p.date_from = fFrom;
    if (fTo) p.date_to = fTo;
    return p;
  }, [fVenue, fStatus, fOrganizer, fType, fFrom, fTo]);

  const loadKpis = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/venue-events/dashboard/kpis`, { params: buildFilterParams() });
      setKpis(r.data || {});
    } catch (e) { toast.error(err(e)); }
  }, [buildFilterParams]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/venue-events`, { params: buildFilterParams() });
      setEvents(r.data.events || []);
    } catch (e) { toast.error(err(e)); }
    finally { setLoading(false); }
  }, [buildFilterParams]);

  const loadAddons = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/venue-events/addons`);
      setAddons(r.data.addons || []);
    } catch (e) { toast.error(err(e)); }
  }, []);

  const loadDirectors = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/funds/directors`);
      setDirectors(r.data.directors || []);
    } catch { /* silently */ }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/venue-events/settings`);
      setSettings(r.data.settings || {});
    } catch { /* silently */ }
  }, []);

  const loadRecipients = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/venue-events/recipients`);
      setRecipients(r.data.recipients || []);
    } catch (e) { toast.error(err(e)); }
  }, []);

  useEffect(() => { loadKpis(); loadEvents(); loadAddons(); loadDirectors(); loadSettings(); loadRecipients(); }, [loadKpis, loadEvents, loadAddons, loadDirectors, loadSettings, loadRecipients]);

  const openAdd = () => { setEditing(null); setShowForm(true); };
  const openEdit = (evt) => { setEditing(evt); setShowForm(true); };

  const removeEvt = async (evt) => {
    if (!window.confirm(`Delete event "${evt.event_name}"?`)) return;
    try {
      await axios.delete(`${API}/venue-events/${evt.id}`);
      toast.success('Event deleted');
      loadEvents(); loadKpis();
    } catch (e) { toast.error(err(e)); }
  };

  const changeStatus = async (evt, newStatus) => {
    if (evt.status === newStatus) return;
    // Marking an event completed should capture the Paid Amount — open the
    // edit form (pre-set to completed) so the admin records what was collected.
    if (newStatus === 'completed') {
      openEdit({ ...evt, status: 'completed' });
      return;
    }
    try {
      await axios.put(`${API}/venue-events/${evt.id}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      loadEvents(); loadKpis();
    } catch (e) { toast.error(err(e)); }
  };

  const sendNotifNow = async (evt) => {
    try {
      const r = await axios.post(`${API}/venue-events/${evt.id}/send-notification`, {});
      toast.success(`Sent ${r.data.sent} / failed ${r.data.failed}`);
    } catch (e) { toast.error(err(e)); }
  };

  const clearFilters = () => {
    setFVenue('all'); setFStatus('all'); setFOrganizer('');
    setFType(''); setFFrom(''); setFTo(''); setFDateRange('all');
  };

  const sortedEvents = useMemo(() => {
    const sorted = [...events].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'quoted') { av = (a.total_amount ?? ((a.base_amount || 0) + (a.addons_amount || 0))); bv = (b.total_amount ?? ((b.base_amount || 0) + (b.addons_amount || 0))); }
      if (sortKey === 'paid_amount') { av = a.paid_amount ?? -1; bv = b.paid_amount ?? -1; }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [events, sortKey, sortDir]);

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ChevronsUpDown className="w-3.5 h-3.5 ml-1 text-slate-400 inline" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 ml-1 text-primary inline" />
      : <ChevronDown className="w-3.5 h-3.5 ml-1 text-primary inline" />;
  };

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6" data-testid="venue-events-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Trophy className="w-6 h-6 sm:w-7 sm:h-7 text-primary" /> 3rd-Party Events
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Book selected courts (or the entire venue) for third-party events, with configurable reminders.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={openAdd} className="bg-primary hover:bg-primary-hover"
                    data-testid="add-venue-event-btn">
              <Plus className="w-4 h-4 mr-1" /> New Event
            </Button>
            <Button size="sm" variant="outline" onClick={() => { loadKpis(); loadEvents(); }}
                    data-testid="refresh-venue-events-btn" title="Refresh">
              <RefreshCw className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Total Events" value={kpis.total} accent="text-slate-900" />
          {STATUS_ORDER.map((s) => (
            <KpiCard key={s} label={s.charAt(0).toUpperCase() + s.slice(1)}
                     value={kpis.by_status?.[s] || 0}
                     accent={{ quoted: 'text-slate-700', booked: 'text-blue-600',
                               cancelled: 'text-rose-600', completed: 'text-emerald-600' }[s]} />
          ))}
          <KpiCard label="Paid Amount" value={inr(kpis.revenue || 0)} accent="text-indigo-600" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="addons" data-testid="tab-addons">Add-on Master</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Reminder Settings</TabsTrigger>
          </TabsList>

          {tab === 'events' && (
            <>
              <Card className="p-3 flex flex-wrap items-end gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Venue</Label>
                  <Select value={fVenue} onValueChange={setFVenue}>
                    <SelectTrigger className="w-[170px]" data-testid="ve-filter-venue"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Venues</SelectItem>
                      {venueNames.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <Select value={fStatus} onValueChange={setFStatus}>
                    <SelectTrigger className="w-[140px]" data-testid="ve-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Organizer</Label>
                  <Input value={fOrganizer} onChange={(e) => setFOrganizer(e.target.value)}
                         placeholder="name / mobile / email" className="w-[180px]"
                         data-testid="ve-filter-organizer" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Event Type</Label>
                  <Input value={fType} onChange={(e) => setFType(e.target.value)}
                         placeholder="e.g. Corporate" className="w-[150px]"
                         data-testid="ve-filter-type" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Date Range</Label>
                  <Select value={fDateRange} onValueChange={setFDateRange}>
                    <SelectTrigger className="w-[140px]" data-testid="ve-filter-date-range"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Dates</SelectItem>
                      <SelectItem value="this_month">This Month</SelectItem>
                      <SelectItem value="next_month">Next Month</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="this_year">This Year</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {fDateRange === 'custom' && (<>
                <div>
                  <Label className="text-xs text-slate-500">From</Label>
                  <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)}
                         className="w-[140px]" data-testid="ve-filter-from" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">To</Label>
                  <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)}
                         className="w-[140px]" data-testid="ve-filter-to" />
                </div>
                </>)}
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                  <Button size="sm" onClick={() => { loadEvents(); loadKpis(); }}>
                    <Filter className="w-4 h-4 mr-1" /> Apply
                  </Button>
                </div>
              </Card>

              <Card className="p-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[960px]">
                  <thead className="bg-slate-50 border-b">
                    <tr className="text-left text-slate-600">
                      <th className="px-3 py-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('event_name')}>
                        Event <SortIcon col="event_name" />
                      </th>
                      <th className="px-3 py-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('venue')}>
                        Venue / Courts <SortIcon col="venue" />
                      </th>
                      <th className="px-3 py-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('start_at')}>
                        Start <SortIcon col="start_at" />
                      </th>
                      <th className="px-3 py-3 text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('quoted')}>
                        Quoted <SortIcon col="quoted" />
                      </th>
                      <th className="px-3 py-3 text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('paid_amount')}>
                        Paid Amount <SortIcon col="paid_amount" />
                      </th>
                      <th className="px-3 py-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('organizer')}>
                        Organizer <SortIcon col="organizer" />
                      </th>
                      <th className="px-3 py-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('status')}>
                        Status <SortIcon col="status" />
                      </th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody data-testid="events-tbody">
                    {loading ? (
                      <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">
                        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
                    ) : sortedEvents.length === 0 ? (
                      <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">
                        No events. Click <span className="font-medium">New Event</span> to add one.</td></tr>
                    ) : sortedEvents.map((evt) => (
                      <tr key={evt.id} className="border-b hover:bg-slate-50/60" data-testid={`event-row-${evt.id}`}>
                        <td className="px-3 py-3">
                          <div className="font-medium text-slate-900">{evt.event_name}</div>
                          {evt.event_type && <div className="text-xs text-slate-500">{evt.event_type}</div>}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {evt.venue}
                          <div className="text-xs text-slate-500">
                            {evt.is_entire_venue ? 'Entire venue' : `${(evt.court_ids || []).length} court(s)`}
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">{fmtDateTime(evt.start_at)}</td>
                        <td className="px-3 py-3 text-right font-semibold">{inr(
                          evt.total_amount ?? ((evt.base_amount || 0) + (evt.addons_amount || 0))
                        )}</td>
                        <td className="px-3 py-3 text-right font-semibold text-emerald-700" data-testid={`event-paid-${evt.id}`}>
                          {evt.paid_amount === null || evt.paid_amount === undefined || evt.paid_amount === ''
                            ? <span className="text-slate-400 font-normal">—</span>
                            : inr(evt.paid_amount)}
                        </td>
                        <td className="px-3 py-3">
                          {evt.organizer_name}
                          <div className="text-xs text-slate-500">{evt.organizer_mobile}</div>
                        </td>
                        <td className="px-3 py-3">
                          <Select value={evt.status} onValueChange={(v) => changeStatus(evt, v)}>
                            <SelectTrigger className={`h-8 w-[120px] text-xs ${STATUS_COLORS[evt.status] || ''}`}
                                           data-testid={`event-status-${evt.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => sendNotifNow(evt)}
                                  title="Send WhatsApp Notification"
                                  data-testid={`notify-event-${evt.id}`}>
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(evt)}
                                  data-testid={`edit-event-${evt.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => removeEvt(evt)}
                                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                  data-testid={`delete-event-${evt.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )}

          {tab === 'addons' && (
            <AddonsPanel addons={addons} onChanged={loadAddons} />
          )}

          {tab === 'settings' && (
            <>
              <SettingsPanel settings={settings} onSaved={(s) => setSettings(s)} />
              <RecipientsPanel recipients={recipients} onChanged={loadRecipients} />
            </>
          )}
        </Tabs>

        <EventFormDialog open={showForm} onClose={() => setShowForm(false)}
                         initial={editing} addons={addons}
                         directors={directors} venueNames={venueNames}
                         existingEvents={events}
                         onSaved={() => { setShowForm(false); loadEvents(); loadKpis(); }} />
      </div>
    </DashboardLayout>
  );
}


function KpiCard({ label, value, accent }) {
  return (
    <Card className="p-3 sm:p-4">
      <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg sm:text-2xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</div>
    </Card>
  );
}


function EventFormDialog({ open, onClose, initial, addons, directors, venueNames, existingEvents, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({});
  const [courts, setCourts] = useState([]);
  const [saving, setSaving] = useState(false);

  // Unique organizer names harvested from historical events for autocomplete.
  const organizerSuggestions = useMemo(() => {
    const set = new Set();
    (existingEvents || []).forEach((e) => {
      if (e.organizer_name) set.add(e.organizer_name.trim());
    });
    return Array.from(set).sort();
  }, [existingEvents]);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        ...initial,
        start_at: toDatetimeLocal(initial.start_at),
        end_at: toDatetimeLocal(initial.end_at),
      });
    } else {
      setForm({
        event_name: '', event_type: '',
        organizer_name: '', organizer_mobile: '', organizer_email: '',
        venue: '', is_entire_venue: false, court_ids: [],
        start_at: '', end_at: '',
        pricing_type: 'fixed',
        base_price: 0, hourly_rate: 0, per_court_rate: 0,
        add_ons: [], remarks: '', status: 'quoted', paid_amount: '',
        notify_director_ids: [],
      });
    }
  }, [open, initial]);

  // Load courts when venue changes
  useEffect(() => {
    if (!form.venue) { setCourts([]); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/masters/venues`);
        const venueDoc = (r.data.venues || []).find((v) => v.name === form.venue);
        if (venueDoc) {
          const cr = await axios.get(`${API}/masters/courts`, { params: { venue_id: venueDoc.id, is_active: true } });
          setCourts(cr.data.courts || []);
        } else {
          setCourts([]);
        }
      } catch { setCourts([]); }
    })();
  }, [form.venue]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When start_at changes, auto-sync end_at's DATE part to the same day
  // (preserving whatever time the user has already typed there). If end_at
  // is empty, copy start_at into it so the user only edits the time.
  const setStart = (v) => {
    setForm((f) => {
      const next = { ...f, start_at: v };
      if (!v) return next;
      if (!f.end_at) {
        next.end_at = v;
      } else {
        const startDate = v.split('T')[0];
        const [, endTime] = f.end_at.split('T');
        if (endTime && f.end_at.split('T')[0] !== startDate) {
          next.end_at = `${startDate}T${endTime}`;
        }
      }
      return next;
    });
  };
  const toggleCourt = (id) => {
    setForm((f) => ({
      ...f, is_entire_venue: false,
      court_ids: (f.court_ids || []).includes(id)
        ? f.court_ids.filter((x) => x !== id)
        : [...(f.court_ids || []), id],
    }));
  };
  const toggleDirector = (id) => {
    setForm((f) => ({
      ...f,
      notify_director_ids: (f.notify_director_ids || []).includes(id)
        ? f.notify_director_ids.filter((x) => x !== id)
        : [...(f.notify_director_ids || []), id],
    }));
  };
  const selectAllDirectors = () => set('notify_director_ids', directors.map((d) => d.id));
  const clearAllDirectors = () => set('notify_director_ids', []);

  const addAddon = () => set('add_ons', [...(form.add_ons || []), { name: '', price: 0, qty: 1 }]);
  const removeAddon = (i) => set('add_ons', (form.add_ons || []).filter((_, idx) => idx !== i));
  const setAddon = (i, patch) => set('add_ons', (form.add_ons || []).map((a, idx) => idx === i ? { ...a, ...patch } : a));

  // Live total. Per-court rate = rate × #courts × #hours; per-hour = rate × #hours.
  const totals = useMemo(() => {
    const ptype = form.pricing_type;
    const s = form.start_at ? new Date(form.start_at) : null;
    const e = form.end_at ? new Date(form.end_at) : null;
    const hours = (s && e && e > s) ? (e - s) / 3600000 : 0;
    let base = 0;
    if (ptype === 'fixed') base = Number(form.base_price || 0);
    else if (ptype === 'per_hour') base = Number(form.hourly_rate || 0) * hours;
    else if (ptype === 'per_court') {
      const n = form.is_entire_venue ? courts.length : (form.court_ids || []).length;
      base = Number(form.per_court_rate || 0) * n * hours;
    }
    const addonsAmt = (form.add_ons || []).reduce((sum, a) => sum + Number(a.price || 0) * Number(a.qty || 1), 0);
    return { base, addons: addonsAmt, total: base + addonsAmt, hours };
  }, [form, courts]);

  // Soft clash guard — poll the availability API when start/end/venue/courts
  // change so we can warn the admin (but not block) that some of the
  // selected courts already have bookings inside the event window.
  const [clashes, setClashes] = useState([]);
  const clashDebounce = useRef(null);
  useEffect(() => {
    clearTimeout(clashDebounce.current);
    if (!form.venue || !form.start_at || !form.end_at) { setClashes([]); return; }
    const courtIdList = form.is_entire_venue
      ? courts.map((c) => c.id)
      : (form.court_ids || []);
    if (courtIdList.length === 0) { setClashes([]); return; }
    clashDebounce.current = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/venue-events/availability-check`, {
          venue: form.venue,
          court_ids: courtIdList,
          start_at: new Date(form.start_at).toISOString(),
          end_at: new Date(form.end_at).toISOString(),
          exclude_event_id: initial?.id || null,
        });
        setClashes(r.data.clashes || []);
      } catch { setClashes([]); }
    }, 500);
    return () => clearTimeout(clashDebounce.current);
  }, [form.venue, form.start_at, form.end_at, form.is_entire_venue, form.court_ids, courts, initial?.id]);

  const submit = async () => {
    if (!form.event_name || !form.organizer_name || !form.venue || !form.start_at || !form.end_at) {
      return toast.error('Fill in event name, organizer, venue, and start/end');
    }
    if (!form.is_entire_venue && (form.court_ids || []).length === 0) {
      return toast.error('Select the entire venue or at least one court');
    }
    if (form.status === 'completed' && (form.paid_amount === '' || form.paid_amount === null || form.paid_amount === undefined)) {
      return toast.error('Enter the Paid Amount to mark this event completed');
    }
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        paid_amount: form.paid_amount === '' || form.paid_amount === undefined || form.paid_amount === null
          ? null : Number(form.paid_amount),
      };
      if (isEdit) {
        await axios.put(`${API}/venue-events/${initial.id}`, payload);
        toast.success('Event updated');
      } else {
        const r = await axios.post(`${API}/venue-events`, payload);
        const n = r.data.notification || {};
        toast.success(`Event created · WA sent ${n.sent || 0}/${(n.sent || 0) + (n.failed || 0) + (n.skipped || 0)}`);
      }
      onSaved && onSaved();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            {isEdit ? 'Edit Event' : 'New 3rd-Party Event'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Event Name *</Label>
            <Input value={form.event_name || ''} onChange={(e) => set('event_name', e.target.value)}
                   data-testid="event-name-input" />
          </div>
          <div>
            <Label>Event Type</Label>
            <Input value={form.event_type || ''} onChange={(e) => set('event_type', e.target.value)}
                   placeholder="e.g. Corporate, School, Party"
                   data-testid="event-type-input" />
          </div>
          <div>
            <Label>Organizer Name *</Label>
            <Input value={form.organizer_name || ''} onChange={(e) => set('organizer_name', e.target.value)}
                   list="organizer-suggestions"
                   data-testid="organizer-name-input" />
            <datalist id="organizer-suggestions">
              {organizerSuggestions.map((n) => <option key={n} value={n} />)}
            </datalist>
          </div>
          <div>
            <Label>Organizer Mobile</Label>
            <Input value={form.organizer_mobile || ''} onChange={(e) => set('organizer_mobile', e.target.value)}
                   data-testid="organizer-mobile-input" />
          </div>
          <div className="sm:col-span-2">
            <Label>Organizer Email</Label>
            <Input type="email" value={form.organizer_email || ''} onChange={(e) => set('organizer_email', e.target.value)}
                   data-testid="organizer-email-input" />
          </div>
          <div>
            <Label>Venue *</Label>
            <Select value={form.venue || ''} onValueChange={(v) => set('venue', v)}>
              <SelectTrigger data-testid="event-venue-select"><SelectValue placeholder="Select venue" /></SelectTrigger>
              <SelectContent>
                {venueNames.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status || 'quoted'} onValueChange={(v) => set('status', v)}>
              <SelectTrigger data-testid="event-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Court selection */}
          <div className="sm:col-span-2 border border-slate-200 rounded-md p-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={!!form.is_entire_venue}
                     onChange={(e) => set('is_entire_venue', e.target.checked)}
                     data-testid="entire-venue-check" />
              <span className="font-medium">Book the entire venue</span>
              {form.is_entire_venue && courts.length > 0 && (
                <span className="text-xs text-slate-500">({courts.length} courts)</span>
              )}
            </label>
            {!form.is_entire_venue && courts.length > 0 && (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-sm">
                {courts.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 cursor-pointer"
                         data-testid={`court-option-${c.id}`}>
                    <input type="checkbox" checked={(form.court_ids || []).includes(c.id)}
                           onChange={() => toggleCourt(c.id)} />
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            )}
            {!form.is_entire_venue && form.venue && courts.length === 0 && (
              <div className="mt-2 text-xs text-slate-400">No active courts for this venue.</div>
            )}
          </div>

          <div>
            <Label>Start *</Label>
            <Input type="datetime-local" value={form.start_at || ''}
                   onChange={(e) => setStart(e.target.value)}
                   disabled={form.status === 'completed'}
                   data-testid="event-start-input" />
          </div>
          <div>
            <Label>End *</Label>
            <Input type="datetime-local" value={form.end_at || ''}
                   onChange={(e) => set('end_at', e.target.value)}
                   disabled={form.status === 'completed'}
                   data-testid="event-end-input" />
          </div>

          {/* Pricing */}
          <div>
            <Label>Pricing Type</Label>
            <Select value={form.pricing_type || 'fixed'} onValueChange={(v) => set('pricing_type', v)}>
              <SelectTrigger data-testid="pricing-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed Price</SelectItem>
                <SelectItem value="per_hour">Per Hour</SelectItem>
                <SelectItem value="per_court">Per Court</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.pricing_type === 'fixed' && (
            <div>
              <Label>Base Price (₹) *</Label>
              <Input type="number" min="0" step="1" value={form.base_price || 0}
                     onChange={(e) => set('base_price', Number(e.target.value))}
                     data-testid="base-price-input" />
            </div>
          )}
          {form.pricing_type === 'per_hour' && (
            <div>
              <Label>Hourly Rate (₹) *</Label>
              <Input type="number" min="0" step="1" value={form.hourly_rate || 0}
                     onChange={(e) => set('hourly_rate', Number(e.target.value))}
                     data-testid="hourly-rate-input" />
            </div>
          )}
          {form.pricing_type === 'per_court' && (
            <div>
              <Label>Rate per Court (₹) *</Label>
              <Input type="number" min="0" step="1" value={form.per_court_rate || 0}
                     onChange={(e) => set('per_court_rate', Number(e.target.value))}
                     data-testid="per-court-rate-input" />
            </div>
          )}

          {/* Add-ons */}
          <div className="sm:col-span-2 border border-slate-200 rounded-md p-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Add-ons</Label>
              <Button type="button" size="sm" variant="outline" onClick={addAddon} data-testid="add-addon-row">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            {(form.add_ons || []).length === 0 ? (
              <div className="text-xs text-slate-400 mt-1">No add-ons</div>
            ) : (form.add_ons || []).map((a, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-end">
                <Select value={a.name} onValueChange={(v) => {
                  const master = addons.find((m) => m.name === v);
                  setAddon(i, { name: v, price: master?.price ?? a.price });
                }}>
                  <SelectTrigger className="col-span-6" data-testid={`addon-name-${i}`}>
                    <SelectValue placeholder="Select add-on" />
                  </SelectTrigger>
                  <SelectContent>
                    {addons.map((m) => <SelectItem key={m.id} value={m.name}>{m.name} — ₹{m.price}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" min="0" value={a.price || 0} onChange={(e) => setAddon(i, { price: Number(e.target.value) })}
                       placeholder="Price" className="col-span-3" data-testid={`addon-price-${i}`} />
                <Input type="number" min="1" value={a.qty || 1} onChange={(e) => setAddon(i, { qty: Number(e.target.value) })}
                       placeholder="Qty" className="col-span-2" data-testid={`addon-qty-${i}`} />
                <Button type="button" size="sm" variant="ghost"
                        onClick={() => removeAddon(i)} className="col-span-1 text-rose-600"
                        data-testid={`addon-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>

          {/* Directors to notify */}
          <div className="sm:col-span-2 border border-slate-200 rounded-md p-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Notify Directors</Label>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="outline" onClick={selectAllDirectors}
                        data-testid="notify-select-all">
                  <CheckSquare className="w-3.5 h-3.5 mr-1" /> Select All
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={clearAllDirectors}
                        data-testid="notify-clear-all">
                  <Square className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              </div>
            </div>
            {directors.length === 0 ? (
              <div className="text-xs text-slate-400 mt-1">No directors configured — add them from MAKS Funds → Directors Master.</div>
            ) : (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm">
                {directors.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={(form.notify_director_ids || []).includes(d.id)}
                           onChange={() => toggleDirector(d.id)}
                           data-testid={`notify-director-${d.id}`} />
                    <span>{d.name}</span>
                    <span className="text-xs text-slate-400">{d.mobile}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <Label>Remarks</Label>
            <Textarea rows={2} value={form.remarks || ''} onChange={(e) => set('remarks', e.target.value)}
                      data-testid="event-remarks" />
          </div>

          <div className="sm:col-span-2 bg-slate-50 rounded-md p-3 text-sm">
            <div className="flex justify-between"><span>Base:</span><span>{inr(totals.base)}</span></div>
            {totals.hours > 0 && (
              <div className="text-xs text-slate-500">
                Duration: {totals.hours.toFixed(2)} hrs
                {form.pricing_type === 'per_court' && (form.is_entire_venue ? courts.length : (form.court_ids || []).length) > 0 && (
                  <> · {form.is_entire_venue ? courts.length : (form.court_ids || []).length} court(s)</>
                )}
              </div>
            )}
            <div className="flex justify-between"><span>Add-ons:</span><span>{inr(totals.addons)}</span></div>
            <div className="flex justify-between text-base font-semibold text-slate-900 mt-1 pt-1 border-t">
              <span>Quoted:</span><span data-testid="form-total">{inr(totals.total)}</span>
            </div>
            {clashes.length > 0 && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
                   data-testid="clash-warning">
                <div className="flex items-center gap-1 font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5" /> Court availability warning
                </div>
                <div>{clashes.length} existing booking(s) overlap the event window. You can still save this event.</div>
                <ul className="mt-1 list-disc list-inside space-y-0.5">
                  {clashes.slice(0, 4).map((c, i) => (
                    <li key={i}>{c.court_name || c.court} · {c.start_time || c.start_at} → {c.end_time || c.end_at} ({c.source || c.type})</li>
                  ))}
                  {clashes.length > 4 && <li>… and {clashes.length - 4} more</li>}
                </ul>
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="event-paid-amount">Paid Amount {form.status === 'completed' && <span className="text-rose-500">*</span>}</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
              <Input
                id="event-paid-amount"
                type="number"
                min="0"
                step="0.01"
                className="pl-7"
                placeholder="Total amount paid by the organiser"
                value={form.paid_amount ?? ''}
                onChange={(e) => set('paid_amount', e.target.value)}
                data-testid="event-paid-amount"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">Record the total collected from the organiser (treated as the actual total). Quoted price above is the estimate.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary hover:bg-primary-hover"
                  data-testid="event-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : (isEdit ? 'Save Changes' : 'Create Event')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AddonsPanel({ addons, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [price, setPrice] = useState(0);
  const [saving, setSaving] = useState(false);

  const open = (a) => { setEditing(a); setName(a?.name || ''); setPrice(a?.price ?? 0); setShowForm(true); };
  const submit = async () => {
    if (!name.trim()) return toast.error('Name is required');
    if (saving) return;
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/venue-events/addons/${editing.id}`, { name, price: Number(price) });
        toast.success('Add-on updated');
      } else {
        await axios.post(`${API}/venue-events/addons`, { name, price: Number(price) });
        toast.success('Add-on added');
      }
      setShowForm(false); onChanged && onChanged();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete add-on "${a.name}"?`)) return;
    try {
      await axios.delete(`${API}/venue-events/addons/${a.id}`);
      toast.success('Deleted');
      onChanged && onChanged();
    } catch (e) { toast.error(err(e)); }
  };

  return (
    <>
      <Card className="p-3 flex items-center justify-between">
        <div className="text-sm text-slate-500">Add-ons appear as options in every new event.</div>
        <Button size="sm" onClick={() => open(null)} data-testid="add-addon-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Add-on
        </Button>
      </Card>
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-3">Name</th>
              <th className="px-3 py-3 text-right">Price</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="addons-tbody">
            {addons.length === 0 ? (
              <tr><td colSpan={3} className="px-3 py-8 text-center text-slate-400">No add-ons yet.</td></tr>
            ) : addons.map((a) => (
              <tr key={a.id} className="border-b hover:bg-slate-50/60" data-testid={`addon-row-${a.id}`}>
                <td className="px-3 py-3 font-medium text-slate-800">{a.name}</td>
                <td className="px-3 py-3 text-right">{inr(a.price)}</td>
                <td className="px-3 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => open(a)}
                          data-testid={`edit-addon-${a.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(a)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-1"
                          data-testid={`delete-addon-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Dialog open={showForm} onOpenChange={(o) => !o && setShowForm(false)}>
        <DialogContent data-testid="addon-form-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Add-on' : 'Add Add-on'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. Sound System, Catering"
                     data-testid="addon-name-input" />
            </div>
            <div>
              <Label>Price (₹)</Label>
              <Input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)}
                     data-testid="addon-price-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="addon-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function SettingsPanel({ settings, onSaved }) {
  const [days, setDays] = useState(settings.reminder_days || [7, 3, 1, 0]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDays(settings.reminder_days || [7, 3, 1, 0]); }, [settings]);

  const setLevel = (idx, val) => {
    const next = [...days];
    while (next.length <= idx) next.push(0);
    next[idx] = Number(val);
    setDays(next);
  };
  const removeLevel = (idx) => setDays(days.filter((_, i) => i !== idx));
  const addLevel = () => { if (days.length < 4) setDays([...days, 0]); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/venue-events/settings`, { reminder_days: days });
      toast.success('Reminder settings saved');
      onSaved && onSaved(r.data.settings);
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="ve-settings-card">
      <div>
        <div className="flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-slate-500" />
          <h3 className="font-semibold text-slate-900">Reminder Days (before event)</h3>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Up to <b>4 reminder levels</b>. Each value is &ldquo;days before the event start&rdquo;.
          Reminders fire only when the event status is <b>Booked</b> and stop the moment status changes.        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        {days.map((d, i) => (
          <div key={i} className="flex items-end gap-1">
            <div>
              <Label className="text-xs text-slate-500">Level {i + 1}</Label>
              <Input type="number" min="0" max="365" value={d}
                     onChange={(e) => setLevel(i, e.target.value)}
                     className="w-[100px]"
                     data-testid={`reminder-day-${i}`} />
            </div>
            <Button size="sm" variant="ghost" className="text-rose-600"
                    onClick={() => removeLevel(i)} data-testid={`reminder-remove-${i}`}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        {days.length < 4 && (
          <Button size="sm" variant="outline" onClick={addLevel} data-testid="reminder-add-level">
            <Plus className="w-4 h-4 mr-1" /> Add Level
          </Button>
        )}
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
          Save
        </Button>
      </div>
    </Card>
  );
}


function RecipientsPanel({ recipients, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [role, setRole] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const open = (r) => {
    setEditing(r);
    setName(r?.name || ''); setMobile(r?.mobile || '');
    setRole(r?.role || ''); setIsActive(r ? r.is_active !== false : true);
    setShowForm(true);
  };

  const submit = async () => {
    if (!name.trim() || !mobile.trim()) return toast.error('Name and mobile are required');
    if (saving) return;
    setSaving(true);
    try {
      const payload = { name, mobile, role, is_active: isActive };
      if (editing) {
        await axios.put(`${API}/venue-events/recipients/${editing.id}`, payload);
        toast.success('Recipient updated');
      } else {
        await axios.post(`${API}/venue-events/recipients`, payload);
        toast.success('Recipient added');
      }
      setShowForm(false); onChanged && onChanged();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.name}" from reminder recipients?`)) return;
    try {
      await axios.delete(`${API}/venue-events/recipients/${r.id}`);
      toast.success('Recipient removed');
      onChanged && onChanged();
    } catch (e) { toast.error(err(e)); }
  };

  const toggleActive = async (r) => {
    try {
      await axios.put(`${API}/venue-events/recipients/${r.id}`, { is_active: !(r.is_active !== false) });
      onChanged && onChanged();
    } catch (e) { toast.error(err(e)); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="ve-recipients-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-500" />
            <h3 className="font-semibold text-slate-900">Reminder Peoples</h3>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Everyone here gets every venue-event WhatsApp reminder (creation + scheduled),
            in addition to the directors picked per event.
          </p>
        </div>
        <Button size="sm" onClick={() => open(null)} data-testid="add-recipient-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Recipient
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Mobile</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Active</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="recipients-tbody">
            {recipients.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">
                No recipients yet. Click <span className="font-medium">Add Recipient</span> to add one.</td></tr>
            ) : recipients.map((r) => (
              <tr key={r.id} className="border-b hover:bg-slate-50/60" data-testid={`recipient-row-${r.id}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                <td className="px-3 py-2 text-slate-700">{r.mobile}</td>
                <td className="px-3 py-2 text-slate-500">{r.role || '—'}</td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => toggleActive(r)}
                    data-testid={`recipient-active-toggle-${r.id}`}
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      r.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {r.is_active !== false ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => open(r)}
                          data-testid={`edit-recipient-${r.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-1"
                          data-testid={`delete-recipient-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={showForm} onOpenChange={(o) => !o && setShowForm(false)}>
        <DialogContent data-testid="recipient-form-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Recipient' : 'Add Recipient'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. Rahul Sharma"
                     data-testid="recipient-name-input" />
            </div>
            <div>
              <Label>Mobile *</Label>
              <Input value={mobile} onChange={(e) => setMobile(e.target.value)}
                     placeholder="10-digit mobile number"
                     data-testid="recipient-mobile-input" />
            </div>
            <div>
              <Label>Role / Designation</Label>
              <Input value={role} onChange={(e) => setRole(e.target.value)}
                     placeholder="e.g. Operations Manager"
                     data-testid="recipient-role-input" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
                     data-testid="recipient-active-input" />
              <span>Active (receives reminders)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="recipient-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
